import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

export const fetchAssignments = createAsyncThunk(
  "deviceAssignments/fetch",
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/users/${userId}/assignments`);
      return { userId, ...data.data };
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load assignments"));
    }
  }
);

export const searchDevices = createAsyncThunk(
  "deviceAssignments/search",
  async ({ q, userId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/devices/search`, {
        params: { q, userId, limit: 25 },
      });
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Search failed"));
    }
  }
);

export const assignDevice = createAsyncThunk(
  "deviceAssignments/assign",
  async ({ userId, deviceId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/users/${userId}/assignments`, { deviceId });
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Assign failed"));
    }
  }
);

export const removeDevice = createAsyncThunk(
  "deviceAssignments/remove",
  async ({ userId, deviceId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/users/${userId}/assignments/${deviceId}`
      );
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Remove failed"));
    }
  }
);

export const moveDevice = createAsyncThunk(
  "deviceAssignments/move",
  async ({ userId, deviceId, direction }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/users/${userId}/assignments/${deviceId}/order`,
        { direction }
      );
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Reorder failed"));
    }
  }
);

const initialState = {
  userId: null,
  user: null,
  company: null,
  assigned: [],
  loading: false,
  saving: false,
  search: {
    type: "company",
    q: "",
    results: [],
    loading: false,
  },
  error: null,
};

const slice = createSlice({
  name: "deviceAssignments",
  initialState,
  reducers: {
    resetForUser(state, { payload }) {
      if (state.userId !== payload) {
        state.userId = payload;
        state.user = null;
        state.company = null;
        state.assigned = [];
        state.search = { ...initialState.search };
        state.error = null;
      }
    },
    setSearchType(state, { payload }) {
      state.search.type = payload;
      state.search.results = [];
    },
    setSearchQuery(state, { payload }) {
      state.search.q = payload;
    },
    clearSearchResults(state) {
      state.search.results = [];
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    const setItems = (s, a) => {
      s.assigned = a.payload;
      const ids = new Set(a.payload.map((d) => d.device_id));
      s.search.results = s.search.results.map((r) => ({
        ...r,
        already_assigned: ids.has(r.device_id),
      }));
    };

    b.addCase(fetchAssignments.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchAssignments.fulfilled, (s, a) => {
        s.loading = false;
        s.userId = a.payload.userId;
        s.user = a.payload.user;
        s.company = a.payload.company;
        s.assigned = a.payload.items;
        const ids = new Set(a.payload.items.map((d) => d.device_id));
        s.search.results = s.search.results.map((r) => ({
          ...r,
          already_assigned: ids.has(r.device_id),
        }));
      })
      .addCase(fetchAssignments.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(searchDevices.pending, (s) => {
        s.search.loading = true;
      })
      .addCase(searchDevices.fulfilled, (s, a) => {
        s.search.loading = false;
        const assignedIds = new Set(s.assigned.map((d) => d.device_id));
        s.search.results = a.payload.map((r) => ({
          ...r,
          already_assigned: assignedIds.has(r.device_id),
        }));
      })
      .addCase(searchDevices.rejected, (s, a) => {
        s.search.loading = false;
        s.error = a.payload;
      })
      .addCase(assignDevice.pending, (s) => {
        s.saving = true;
      })
      .addCase(assignDevice.fulfilled, (s, a) => {
        s.saving = false;
        setItems(s, a);
      })
      .addCase(assignDevice.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(removeDevice.pending, (s) => {
        s.saving = true;
      })
      .addCase(removeDevice.fulfilled, (s, a) => {
        s.saving = false;
        setItems(s, a);
      })
      .addCase(removeDevice.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(moveDevice.pending, (s) => {
        s.saving = true;
      })
      .addCase(moveDevice.fulfilled, (s, a) => {
        s.saving = false;
        s.assigned = a.payload;
      })
      .addCase(moveDevice.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      });
  },
});

export const {
  resetForUser,
  setSearchType,
  setSearchQuery,
  clearSearchResults,
  clearError,
} = slice.actions;
export default slice.reducer;
